"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Loader2, Check } from "lucide-react";
import { Form as FormProvider } from "@/components/ui/form";
import { Button } from "@/components/ui/button.new";
import { H2 } from "@/components/ui/typography";
import { useTranslations } from "next-intl";
import { useDemoRequestForm } from "./use-demo-request-form";
import { FormField } from "./form-fields";
type OnSubmitCallback = (formData: FormData) => Promise<{
  errors?: string[];
  success?: boolean;
}>;
type Props = Readonly<{
  onSubmit: OnSubmitCallback;
}>;
const Checklist = () => {
  const $ = _c(12);
  const t = useTranslations("welcome.demoRequestForm");
  let t0;
  if ($[0] !== t) {
    t0 = t("checklist.personalizedDemo");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("checklist.getAnswers");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("checklist.qaExpert");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = [t0, t1, t2];
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const items = t3;
  let t4;
  if ($[10] !== items) {
    t4 = <div className="flex flex-col gap-6">{items.map(_temp)}</div>;
    $[10] = items;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
const SubmitButton = t0 => {
  const $ = _c(9);
  const {
    isSubmitting
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] !== isSubmitting) {
    t1 = isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />;
    $[0] = isSubmitting;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isSubmitting || $[3] !== t) {
    t2 = isSubmitting ? t("submittingButton") : t("submitButton");
    $[2] = isSubmitting;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isSubmitting || $[6] !== t1 || $[7] !== t2) {
    t3 = <Button type="submit" form="demo-request-form" disabled={isSubmitting} purpose="primary" shape="pill" className="w-full">{t1}{t2}</Button>;
    $[5] = isSubmitting;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const Container = t0 => {
  const $ = _c(14);
  const {
    children,
    isSubmitting
  } = t0;
  const t = useTranslations("welcome.demoRequestForm");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      background: "radial-gradient(ellipse at center, rgba(234,236,253,1) 0%, rgba(249,250,254,1) 72.68%, rgba(255,255,255,1) 100%)"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <H2 className="text-primary text-center text-2xl font-medium">{t2}</H2>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Checklist />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <div className="space-y-6">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isSubmitting) {
    t6 = <SubmitButton isSubmitting={isSubmitting} />;
    $[8] = isSubmitting;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== children || $[11] !== t5 || $[12] !== t6) {
    t7 = <div id="demo-request-form-container" data-testid="demo-request-form-container" className="relative space-y-8 overflow-hidden rounded-xl p-10 lg:p-12" style={t1}><div className="relative z-10 space-y-8">{t5}{children}{t6}</div></div>;
    $[10] = children;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
export function Form({
  onSubmit
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleFormSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(formData);
      return result;
    } finally {
      setIsSubmitting(false);
    }
  };
  const {
    form,
    handleSubmit
  } = useDemoRequestForm({
    onSubmit: handleFormSubmit
  });
  return <Container isSubmitting={isSubmitting}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit} id="demo-request-form" className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField.FirstName disabled={isSubmitting} />
            <FormField.LastName disabled={isSubmitting} />
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField.Email disabled={isSubmitting} />
            <FormField.Company disabled={isSubmitting} />
          </div>
          <FormField.VenueType disabled={isSubmitting} />
          <FormField.Comments disabled={isSubmitting} />
        </form>
      </FormProvider>
    </Container>;
}
function _temp(item, index) {
  return <div key={index} className="flex items-center gap-6"><div className="flex h-4 w-4 shrink-0 items-center justify-center rounded"><Check className="text-primary h-4 w-4" /></div><p className="text-foreground text-sm leading-5 font-medium">{item}</p></div>;
}